import { useState, useEffect } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import type { ColumnDef } from '@tanstack/react-table'
import {
  ArrowLeft,
  MoreHorizontal,
  Plus,
  Pencil,
  Trash2,
  Calendar,
  Mail,
  Users,
} from 'lucide-react'
import { useAuth } from '@/context/AuthContext'
import { useOrg } from '@/context/OrgContext'
import {
  useOrganization,
  useDeleteOrganization,
  useUsers,
  useDeleteUser,
} from '@/hooks'
import {
  PageHeader,
  EmptyState,
  DataTable,
  SortableHeader,
  ConfirmDialog,
  NotFound,
  Forbidden,
} from '@/components'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import type { User, Role } from '@/types'
import { OrgFormDialog } from './OrgFormDialog'
import { UserFormDialog } from '../users/UserFormDialog'

const planTierLabels: Record<string, string> = {
  free: 'Free',
  starter: 'Starter',
  professional: 'Professional',
  enterprise: 'Enterprise',
}

const planTierVariants: Record<string, 'default' | 'secondary' | 'outline'> = {
  free: 'outline',
  starter: 'secondary',
  professional: 'default',
  enterprise: 'default',
}

const roleLabels: Record<Role, string> = {
  super_admin: 'Super Admin',
  admin: 'Admin',
  employee: 'Employee',
  viewer: 'Viewer',
}

const roleVariants: Record<Role, 'default' | 'secondary' | 'outline'> = {
  super_admin: 'default',
  admin: 'default',
  employee: 'secondary',
  viewer: 'outline',
}

export function OrgDetailPage() {
  const { orgId } = useParams<{ orgId: string }>()
  const navigate = useNavigate()
  const { user } = useAuth()
  const { selectOrg, effectiveOrgId } = useOrg()

  const {
    data: org,
    isLoading: orgLoading,
    error: orgError,
  } = useOrganization(orgId ?? '')

  // Auto-select this org so API calls (users, mutations) target it.
  // This intentionally persists after navigation — the super admin's last-viewed
  // org remains selected, which is the expected UX for the org switcher.
  useEffect(() => {
    if (org && org.id !== effectiveOrgId) {
      selectOrg(org)
    }
  }, [org, effectiveOrgId, selectOrg])

  const {
    data: users,
    isLoading: usersLoading,
    error: usersError,
  } = useUsers(effectiveOrgId)
  const deleteOrgMutation = useDeleteOrganization()
  const deleteUserMutation = useDeleteUser(effectiveOrgId)

  const [editDialogOpen, setEditDialogOpen] = useState(false)
  const [deleteOrgDialogOpen, setDeleteOrgDialogOpen] = useState(false)
  const [createUserDialogOpen, setCreateUserDialogOpen] = useState(false)
  const [editUser, setEditUser] = useState<User | null>(null)
  const [deleteUser, setDeleteUser] = useState<User | null>(null)

  if (user?.role !== 'super_admin') {
    return <Forbidden />
  }

  const userColumns: ColumnDef<User>[] = [
    {
      accessorKey: 'name',
      header: ({ column }) => (
        <SortableHeader column={column}>Name</SortableHeader>
      ),
      cell: ({ row }) => (
        <span className="font-medium">{row.getValue('name') || '—'}</span>
      ),
    },
    {
      accessorKey: 'email',
      header: ({ column }) => (
        <SortableHeader column={column}>Email</SortableHeader>
      ),
    },
    {
      accessorKey: 'role',
      header: ({ column }) => (
        <SortableHeader column={column}>Role</SortableHeader>
      ),
      cell: ({ row }) => {
        const role = row.getValue('role') as Role
        return (
          <Badge variant={roleVariants[role]}>
            {roleLabels[role]}
          </Badge>
        )
      },
    },
    {
      accessorKey: 'created_at',
      header: ({ column }) => (
        <SortableHeader column={column}>Created</SortableHeader>
      ),
      cell: ({ row }) => {
        const date = new Date(row.getValue('created_at'))
        return date.toLocaleDateString()
      },
    },
    {
      id: 'actions',
      cell: ({ row }) => {
        const rowUser = row.original
        const canModify = rowUser.id !== user?.id && rowUser.role !== 'super_admin'

        if (!canModify) return null

        return (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon">
                <span className="sr-only">Open menu</span>
                <MoreHorizontal className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => setEditUser(rowUser)}>
                <Pencil className="mr-2 h-4 w-4" />
                Edit
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => setDeleteUser(rowUser)}
                className="text-destructive focus:text-destructive"
              >
                <Trash2 className="mr-2 h-4 w-4" />
                Delete
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )
      },
    },
  ]

  if (orgLoading) {
    return <div className="text-muted-foreground">Loading organization...</div>
  }

  if (orgError || !org) {
    return <NotFound onBack={() => navigate('/organizations')} />
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" asChild>
          <Link to="/organizations">
            <ArrowLeft className="h-4 w-4" />
          </Link>
        </Button>
        <PageHeader
          title={org.name}
          description="Organization details and users"
          actions={
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setEditDialogOpen(true)}>
                <Pencil className="mr-2 h-4 w-4" />
                Edit
              </Button>
              <Button
                variant="destructive"
                onClick={() => setDeleteOrgDialogOpen(true)}
              >
                <Trash2 className="mr-2 h-4 w-4" />
                Delete
              </Button>
            </div>
          }
        />
      </div>

      {/* Organization Info Card */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Organization Information</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center gap-2">
            <Mail className="h-4 w-4 text-muted-foreground" />
            <span>{org.contact_email}</span>
          </div>
          <div className="flex items-center gap-2">
            <Badge variant={planTierVariants[org.plan_tier] ?? 'outline'}>
              {planTierLabels[org.plan_tier] ?? org.plan_tier}
            </Badge>
          </div>
          {(org.primary_color || org.secondary_color) && (
            <div className="flex items-center gap-3">
              {org.primary_color && (
                <div className="flex items-center gap-2">
                  <div
                    className="h-4 w-4 rounded border"
                    style={{ backgroundColor: org.primary_color }}
                  />
                  <span className="text-sm text-muted-foreground">
                    Primary: {org.primary_color}
                  </span>
                </div>
              )}
              {org.secondary_color && (
                <div className="flex items-center gap-2">
                  <div
                    className="h-4 w-4 rounded border"
                    style={{ backgroundColor: org.secondary_color }}
                  />
                  <span className="text-sm text-muted-foreground">
                    Secondary: {org.secondary_color}
                  </span>
                </div>
              )}
            </div>
          )}
          <div className="flex items-center gap-2">
            <Calendar className="h-4 w-4 text-muted-foreground" />
            <span className="text-muted-foreground">
              Created {new Date(org.created_at).toLocaleDateString()}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <Badge variant="secondary">
              {users?.length ?? 0} {users?.length === 1 ? 'user' : 'users'}
            </Badge>
          </div>
        </CardContent>
      </Card>

      {/* Users Section */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold">Users</h2>
          <Button onClick={() => setCreateUserDialogOpen(true)}>
            <Plus className="mr-2 h-4 w-4" />
            Add User
          </Button>
        </div>

        {usersLoading ? (
          <div className="text-muted-foreground">Loading users...</div>
        ) : usersError ? (
          <div className="text-destructive">
            Failed to load users: {usersError.message}
          </div>
        ) : users && users.length > 0 ? (
          <DataTable
            columns={userColumns}
            data={users}
            filterColumn="email"
            filterPlaceholder="Filter users by email..."
          />
        ) : (
          <EmptyState
            icon={<Users className="h-12 w-12" />}
            title="No users"
            description="Add users to this organization."
            action={
              <Button onClick={() => setCreateUserDialogOpen(true)}>
                <Plus className="mr-2 h-4 w-4" />
                Add User
              </Button>
            }
          />
        )}
      </div>

      {/* Dialogs */}
      <OrgFormDialog
        open={editDialogOpen}
        onOpenChange={setEditDialogOpen}
        organization={org}
      />

      <ConfirmDialog
        open={deleteOrgDialogOpen}
        onOpenChange={setDeleteOrgDialogOpen}
        title="Delete Organization"
        description={`Are you sure you want to delete "${org.name}"? This will delete all buildings, rooms, assets, users, and reports associated with this organization. This action cannot be undone.`}
        confirmText="Delete"
        destructive
        onConfirm={() => {
          deleteOrgMutation.mutate(org.id, {
            onSuccess: () => navigate('/organizations'),
          })
        }}
        isLoading={deleteOrgMutation.isPending}
      />

      <UserFormDialog
        open={createUserDialogOpen}
        onOpenChange={setCreateUserDialogOpen}
      />

      {editUser && (
        <UserFormDialog
          open={!!editUser}
          onOpenChange={(open) => !open && setEditUser(null)}
          user={editUser}
        />
      )}

      <ConfirmDialog
        open={!!deleteUser}
        onOpenChange={(open) => !open && setDeleteUser(null)}
        title="Delete User"
        description={`Are you sure you want to delete "${deleteUser?.name || deleteUser?.email}"? This action cannot be undone.`}
        confirmText="Delete"
        destructive
        onConfirm={() => {
          if (deleteUser) {
            deleteUserMutation.mutate(deleteUser.id, {
              onSuccess: () => setDeleteUser(null),
            })
          }
        }}
        isLoading={deleteUserMutation.isPending}
      />
    </div>
  )
}
